"use client"

import { useCallback, useRef, useState } from "react"
import { ArrowRight, Gauge, Loader2, Plus, Search, Trophy, X } from "lucide-react"
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { track } from "@/lib/analytics/track"

const MAX_COMPETITORS = 3
const signUpHref = "/sign-up"

interface DrResult {
  domain: string
  dr: number | null
}

/** DR band label + color, mirroring how Ahrefs buckets authority. */
function drBand(dr: number): { label: string; color: string } {
  if (dr >= 70) return { label: "Authoritative", color: "#059669" }
  if (dr >= 50) return { label: "Strong", color: "#16a34a" }
  if (dr >= 30) return { label: "Established", color: "#d97706" }
  if (dr >= 10) return { label: "Emerging", color: "#ea580c" }
  return { label: "New", color: "#dc2626" }
}

export function DrChecker() {
  const [primary, setPrimary] = useState("")
  const [competitors, setCompetitors] = useState<string[]>([])
  const [results, setResults] = useState<DrResult[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [primaryError, setPrimaryError] = useState(false)

  const turnstileRef = useRef<TurnstileInstance | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

  const addCompetitor = () => {
    if (competitors.length < MAX_COMPETITORS) setCompetitors([...competitors, ""])
  }
  const updateCompetitor = (i: number, v: string) => {
    setCompetitors(competitors.map((c, idx) => (idx === i ? v : c)))
  }
  const removeCompetitor = (i: number) => {
    setCompetitors(competitors.filter((_, idx) => idx !== i))
  }

  const handleCheck = useCallback(async () => {
    if (!primary.trim()) {
      setPrimaryError(true)
      return
    }

    const domains = [primary.trim(), ...competitors.map((c) => c.trim()).filter(Boolean)]

    setIsLoading(true)
    setError(null)
    setResults(null)

    try {
      const token = turnstileToken ?? turnstileRef.current?.getResponse() ?? "dev-token"

      // web-arch-allow-direct-fetch: public Turnstile BFF request is local component state.
      const res = await fetch("/api/marketing/tools/dr-checker", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains, turnstileToken: token }),
      })
      const data = await res.json()

      if (!res.ok) {
        if (data.code === "RATE_LIMITED") {
          setError("Too many checks in a short window. Try again in a minute.")
        } else if (data.code === "BOT_DETECTED") {
          setError("Verification failed. Please refresh and try again.")
        } else if (data.code === "INVALID_DOMAIN") {
          setError("That doesn't look like a valid domain. Try e.g. yoursite.com")
        } else {
          setError("Something went wrong. Please try again.")
        }
        return
      }

      const list = (data.results as DrResult[]) ?? []
      setResults(list)

      // Ad-conversion signal: someone checking their DR against competitors is a
      // high-intent SEO buyer — worth retargeting as a Meta 'Lead'.
      track(
        "dr_checker_run",
        { domain_count: list.length, competitors: competitors.filter(Boolean).length },
      )
    } catch {
      setError("Network error. Please check your connection and try again.")
    } finally {
      setIsLoading(false)
      setTurnstileToken(null)
      turnstileRef.current?.reset()
    }
  }, [primary, competitors, turnstileToken])

  return (
    <div className="w-full rounded-2xl border border-border bg-card p-6 text-left shadow-sm sm:p-8">
      <div className="mb-5 flex items-center gap-2">
        <Gauge className="size-4 text-primary" />
        <p className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">
          Free Domain Rating Checker
        </p>
      </div>

      {/* Primary domain */}
      <label
        htmlFor="dr-primary"
        className="mb-2 block text-xs font-medium uppercase tracking-wider text-muted-foreground"
      >
        Your domain <span className="text-destructive">*</span>
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          id="dr-primary"
          type="text"
          inputMode="url"
          placeholder="yoursite.com"
          value={primary}
          onChange={(e) => {
            setPrimary(e.target.value)
            if (primaryError) setPrimaryError(false)
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCheck()
          }}
          className={`h-12 w-full rounded-xl border bg-muted/50 px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 ${
            primaryError
              ? "border-destructive focus:border-destructive focus:ring-destructive"
              : "border-border focus:border-primary focus:ring-primary"
          }`}
        />
        <Button
          className="h-12 shrink-0 rounded-xl sm:w-auto"
          size="lg"
          onClick={handleCheck}
          disabled={isLoading || (!turnstileToken && !!siteKey)}
        >
          {isLoading ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <Search className="size-4" />
              Check DR
            </>
          )}
        </Button>
      </div>
      {primaryError && (
        <p className="mt-1.5 text-xs text-destructive">Enter a domain to check.</p>
      )}

      {/* Competitors */}
      <div className="mt-4 space-y-2">
        {competitors.map((c, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              vs
            </span>
            <input
              type="text"
              inputMode="url"
              placeholder={`competitor${i + 1}.com`}
              value={c}
              onChange={(e) => updateCompetitor(i, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCheck()
              }}
              className="w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => removeCompetitor(i)}
              className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Remove competitor"
            >
              <X className="size-4" />
            </button>
          </div>
        ))}
        {competitors.length < MAX_COMPETITORS && (
          <button
            type="button"
            onClick={addCompetitor}
            className="flex items-center gap-1.5 text-xs font-medium text-primary transition-colors hover:text-primary/80"
          >
            <Plus className="size-3.5" />
            {competitors.length === 0 ? "Compare against competitors" : "Add another competitor"}
          </button>
        )}
      </div>

      {/* Turnstile */}
      {siteKey && (
        <div className="mt-4">
          <Turnstile
            ref={turnstileRef}
            siteKey={siteKey}
            options={{ size: "flexible" }}
            onSuccess={(token) => setTurnstileToken(token)}
          />
        </div>
      )}

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

      {/* Results */}
      {results && results.length > 0 && (
        <DrResults results={results} />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Results: ranked comparison bars + dynamic, gap-aware CTA                   */
/* -------------------------------------------------------------------------- */

function DrResults({ results }: { results: DrResult[] }) {
  const primary = results[0]
  const rated = results.filter((r) => r.dr !== null) as { domain: string; dr: number }[]
  const maxDr = Math.max(1, ...rated.map((r) => r.dr))

  // Rank by DR (desc) for the comparison chart, but remember the primary domain.
  const ranked = [...rated].sort((a, b) => b.dr - a.dr)
  const primaryDr = primary.dr
  const primaryBand = primaryDr !== null ? drBand(primaryDr) : null

  // A single full-width bar carries no information — bars are only meaningful
  // once there's something to compare against.
  const hasComparison = rated.some((r) => r.domain !== primary.domain)

  // Non-primary lookups that failed (network/timeout — distinct from DR 0).
  const failedCompetitors = results
    .filter((r) => r.dr === null && r.domain !== primary.domain)
    .map((r) => r.domain)

  // Gap-aware CTA copy: agitate when behind, reinforce when ahead.
  let ctaHeadline = "DR is just one signal. See your full growth picture."
  let ctaBody =
    "Soku's AI agent audits your backlinks, content, and ads — then runs the campaigns to grow your authority."
  if (primaryDr !== null && hasComparison) {
    const topCompetitor = ranked.find((r) => r.domain !== primary.domain)
    if (topCompetitor && topCompetitor.dr > primaryDr) {
      ctaHeadline = `${topCompetitor.domain} leads you by ${topCompetitor.dr - primaryDr} DR points.`
      ctaBody =
        "Soku's AI agent finds the backlink and content gaps behind that lead — then runs the SEO and ads to close it."
    } else {
      ctaHeadline = `You lead at DR ${primaryDr}. Keep the moat.`
      ctaBody =
        "Soku monitors competitor backlinks and auto-runs campaigns so your authority advantage compounds."
    }
  }

  return (
    <div className="mt-6 space-y-6 border-t border-border pt-6">
      {/* Hero: the DR score is the headline element. */}
      {primaryDr !== null && primaryBand ? (
        <div className="flex items-center gap-4">
          <span
            className="font-mono text-6xl font-bold leading-none tracking-tight sm:text-7xl"
            style={{ color: primaryBand.color }}
          >
            {primaryDr}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-semibold text-foreground">{primary.domain}</span>
              <Badge variant="secondary" className="text-[10px]">
                You
              </Badge>
            </div>
            <p className="text-sm font-medium" style={{ color: primaryBand.color }}>
              {primaryBand.label} authority
            </p>
            <p className="text-xs text-muted-foreground">Ahrefs Domain Rating · scale 0–100</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t reach a rating for {primary.domain}. Try again in a moment.
        </p>
      )}

      {/* Comparison bars — only rendered when there's a competitor to compare. */}
      {hasComparison && (
        <div className="space-y-3">
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            How you stack up
          </p>
          {ranked.map((r, idx) => {
            const isPrimary = r.domain === primary.domain
            const band = drBand(r.dr)
            return (
              <div key={r.domain} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    {idx === 0 && <Trophy className="size-3.5 text-amber-500" />}
                    {r.domain}
                    {isPrimary && (
                      <Badge variant="secondary" className="text-[10px]">
                        You
                      </Badge>
                    )}
                  </span>
                  <span className="font-mono tracking-tight font-semibold" style={{ color: band.color }}>
                    {r.dr}
                  </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${
                      isPrimary ? "" : "opacity-60"
                    }`}
                    style={{ width: `${(r.dr / maxDr) * 100}%`, backgroundColor: band.color }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Competitor lookups that failed. */}
      {failedCompetitors.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Couldn&apos;t reach a rating for: {failedCompetitors.join(", ")}. Try again in a moment.
        </p>
      )}

      {/* Dynamic CTA */}
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
        <p className="text-sm font-semibold text-foreground">{ctaHeadline}</p>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{ctaBody}</p>
        <Button className="mt-3 rounded-full" size="sm" asChild>
          <a href={signUpHref} target="_blank" rel="noopener noreferrer">
            Get your free growth plan
            <ArrowRight className="size-3.5" />
          </a>
        </Button>
      </div>
    </div>
  )
}
